/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ViewTicketAttachment.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { HandleApiActions } from "../../../redux/actions";
import {
  Box,
  Typography,
} from "@mui/material";
import CustomButtonV2 from "../../../components/customComponentsV2/common/button/CustomButtonV2";
import TicketAttachmentUpload from "./TicketAttachmentUpload";
import CustomDataGridV2 from "../../../components/customComponentsV2/common/datagrid/CustomDataGridV2";
import ViewUploadedFile from "../../../components/file-upload/ViewUploadedFile";
import CustomFilterV2 from "../../../components/customComponentsV2/common/filter/CustomFilterV2";
import CustomRadioButtonV2 from "../../../components/customComponentsV2/common/radioButton/CustomRadioButtonV2";
import CustomCheckBoxV2 from "../../../components/customComponentsV2/common/checkBox/CustomCheckBoxV2";
import sortUp from "../../../assets/sortup.png";
import sortDown from "../../../assets/sortdown.png";

function AttachmentList({ artefactDetails, intr_id, tickets_type, fetchTickets }) {
  const dispatch = useDispatch();
  const selectedProjectDetails = useSelector((state) => state?.projectConfigReducer?.selectedProjectDetails);
  const selectedVariantDetails = useSelector((state) => state?.projectConfigReducer?.selectedVariantDetails);

  const [localArtefacts, setLocalArtefacts] = useState(artefactDetails || []);

  useEffect(() => {
    if (artefactDetails && Array.isArray(artefactDetails) && artefactDetails.length > 0) {
      setLocalArtefacts(artefactDetails);
    }
  }, [artefactDetails]);

  const effectiveTicketsType = tickets_type || 2;

  const fetchLocalTickets = async () => {
    try {
      const res = await Promise.resolve(dispatch(
        HandleApiActions({
          api_name: `/ACM/intersections/tickets?project_id=${selectedProjectDetails?.project_id}&variant_id=${selectedVariantDetails?.variant_id}&page_size=25&page_number=1&status=100,101,150,200,300,400,500,600,999&tickets_type=${effectiveTicketsType}&intr_id=${intr_id}`,
          https_method: "GET",
          show_toast: false,
        })
      ));
      if (!res?.error && res?.data && res.data.length > 0) {
        const ticket = res.data.find(t => t.intr_id === intr_id);
        if (ticket?.attachment_details) {
          setLocalArtefacts(ticket.attachment_details);
        } else if (res.data[0].attachment_details) {
          setLocalArtefacts(res.data[0].attachment_details);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleUploadSuccess = async () => {
    await fetchLocalTickets();
    if (fetchTickets) {
      await fetchTickets();
    }
  };

  useEffect(() => {
    if (intr_id) {
      fetchLocalTickets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intr_id, selectedProjectDetails?.project_id, selectedVariantDetails?.variant_id, effectiveTicketsType]);

  const [open, setOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState("");

  const [anchorElFilter, setAnchorElFilter] = useState(null);
  const [openFilter, setOpenFilter] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
    typeGroup: { ATTRIBUTE: true, UPLOADED: true, SNOW: true },
    type_all: true
  });
  const [appliedFilters, setAppliedFilters] = useState({
    sortBy: "created_at",
    sortOrder: "DESC",
    typeGroup: { ATTRIBUTE: true, UPLOADED: true, SNOW: true },
    type_all: true
  });

  const handleFilterClick = (event) => {
    setAnchorElFilter(event.currentTarget);
    setOpenFilter(true);
  };

  const handleFilterClose = () => {
    setAnchorElFilter(null);
    setOpenFilter(false);
  };

  const toggleType = (item, state) => {
    setFilters((prev) => {
      let updatedTypeGroup;
      if (item === "ALL") {
        updatedTypeGroup = {
          ATTRIBUTE: state,
          UPLOADED: state,
          SNOW: state,
        };
      } else {
        updatedTypeGroup = {
          ...prev.typeGroup,
          [item]: state,
        };
      }

      const allState = updatedTypeGroup.ATTRIBUTE && updatedTypeGroup.UPLOADED && updatedTypeGroup.SNOW;

      return {
        ...prev,
        type_all: allState,
        typeGroup: updatedTypeGroup,
      };
    });
  };

  const handleApplyFilters = (action) => {
    if (action === "discard") {
      const reset = {
        sortBy: "created_at",
        sortOrder: "DESC",
        typeGroup: { ATTRIBUTE: true, UPLOADED: true, SNOW: true },
        type_all: true,
      };
      setFilters(reset);
      setAppliedFilters(reset);
      handleFilterClose();
    } else if (action === "apply") {
      setAppliedFilters(filters);
      handleFilterClose();
    } else if (action === "cancel") {
      setFilters(appliedFilters);
      handleFilterClose();
    }
  };

  const filteredAndSortedRows = React.useMemo(() => {
    let result = [...(localArtefacts || [])];
    
    if (!appliedFilters.type_all) {
      result = result.filter(
        (item) => appliedFilters.typeGroup[item?.attachment_type]
      );
    }
    
    result.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      
      if (appliedFilters.sortOrder === "ASC") {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
    
    return result;
  }, [localArtefacts, appliedFilters]);

  const handleView = (file, fileName) => {
    setSelectedFile(file);
    setSelectedFileName(fileName);
    setOpen(true);
  };

  const handleOpenUpload = () => {
    setUploadDialogOpen(true);
  };

  const handleCloseUpload = () => {
    setUploadDialogOpen(false);
  };

  if (!localArtefacts || localArtefacts.length === 0) {
    return (
      <>
       <Box sx={{ display: "flex", justifyContent: "space-between", p: 2 }}>
        <Box></Box>
         <CustomButtonV2 
          btnName="Upload"
          handleOnClick={handleOpenUpload}
        />
       </Box>
      <Typography sx={{ textAlign: "center", p: 2 }}>
        No attachments available
      </Typography>
      <TicketAttachmentUpload open={uploadDialogOpen} handleClose={handleCloseUpload} intr_id={intr_id} fetchTickets={handleUploadSuccess} />
      </>
    );
  }

  const columns = [
    {
      field: "sno",
      headerName: "S.No",
      flex: 0.5,
      renderCell: (params) => {
        return (params.api.getRowIndexRelativeToVisibleRows(params.row.id) || 0) + 1;
      },
    },
    {
      field: "heading",
      headerName: "File Name",
      flex: 1,
      renderCell: (params) => {
        const item = params.row;
        const fileName = item?.attachment_meta?.filename;
        const displayName = item?.attachment_meta?.attachment_meta || fileName || item?.heading || "No file";
        return displayName;
      },
    },
    {
      field: "attachment_type",
      headerName: "Type",
      flex: 1,
    },
    {
      field: "attachment",
      headerName: "Attachment",
      flex: 1,
      renderCell: (params) => {
        const item = params.row;
        const fileName = item?.attachment_meta?.filename || item?.attachment_name;
        const displayName = item?.attachment_meta?.attachment_meta || fileName || item?.heading;
        
        return fileName ? (
          <CustomButtonV2
            variant="contained"
            size="small"
            btnName="View"
            handleOnClick={() => handleView(fileName, displayName)}
          />
        ) : null;
      },
    },
    {
      field: "created_at",
      headerName: "Uploaded At",
      flex: 1,
      renderCell: (params) => {
        const dateString = params.row?.created_at;
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      },
    },
  ];

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", p: 2, gap: 2 }}>
        <CustomFilterV2
          anchorEl={anchorElFilter}
          open={openFilter}
          widthPerColumn="250px"
          handleClick={handleFilterClick}
          handleClose={handleFilterClose}
          handleFilterButton={handleApplyFilters}
          showClearAll={true}
          handleClearAll={() => handleApplyFilters("discard")}
          sections={[
            {
              heading: "Sorting",
              content: (
                <Box>
                  <Box sx={{ p: 2 }}>
                    <CustomRadioButtonV2
                      value={filters.sortBy}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          sortBy: e.target.value,
                        }))
                      }
                      data={[{ value: "created_at", label: "Created At" }]}
                    />
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                    <Box
                      component="button"
                      onClick={() => setFilters((prev) => ({ ...prev, sortOrder: "ASC" }))}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 0,
                        backgroundColor: filters?.sortOrder === "ASC" ? "#26890D" : "transparent",
                        color: filters?.sortOrder === "ASC" ? "#FFFFFF" : "#000000",
                      }}
                    >
                      <img src={sortUp} alt="Sort ASC" style={{ width: 16, height: 16 }} />
                      <Typography variant="body2" sx={{ color: "inherit" }}>
                        Sort Asc - Des
                      </Typography>
                    </Box>
                    <Box
                      component="button"
                      onClick={() => setFilters((prev) => ({ ...prev, sortOrder: "DESC" }))}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        cursor: "pointer",
                        px: 1.5,
                        py: 0.75,
                        borderRadius: 0,
                        backgroundColor: filters?.sortOrder === "DESC" ? "#26890D" : "transparent",
                        color: filters?.sortOrder === "DESC" ? "#FFFFFF" : "#000000",
                      }}
                    >
                      <img src={sortDown} alt="Sort DESC" style={{ width: 16, height: 16 }} />
                      <Typography variant="body2" sx={{ color: "inherit" }}>
                        Sort Des - Asc
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ),
            },
            {
              heading: "Filter By Type",
              content: (
                <Box sx={{ display: "flex", flexDirection: "column", p: 2, gap: 1 }}>
                  {[
                    { key: "ALL", label: "All" },
                    { key: "ATTRIBUTE", label: "Attribute - Type (ATTRIBUTE)" },
                    { key: "UPLOADED", label: "Uploaded - Type (UPLOADED)" },
                    { key: "SNOW", label: "Snow - Type (SNOW)" },
                  ].map((item) => {
                    const checked = item.key === "ALL" ? filters.type_all : filters.typeGroup[item.key];
                    return (
                      <CustomCheckBoxV2
                        key={item.key}
                        label={item.label}
                        value={checked}
                        onChange={(e) => toggleType(item.key, e.target.checked)}
                      />
                    );
                  })}
                </Box>
              ),
            }
          ]}
        />
        <CustomButtonV2 
          btnName="Upload"
          handleOnClick={handleOpenUpload}
        />
      </Box>
      
      <Box sx={{ height: 400, width: "100%", p: 2 }}>
        <CustomDataGridV2
          rows={filteredAndSortedRows}
          columns={columns}
          isMultiLine={true}
        />
      </Box>

      <ViewUploadedFile
        open={open}
        onClose={() => setOpen(false)}
        selectedFile={selectedFile}
        setSelectedFile={setSelectedFile}
        downloadFile={true}
        fileName={selectedFileName}
      />
      <TicketAttachmentUpload open={uploadDialogOpen} handleClose={handleCloseUpload} intr_id={intr_id} fetchTickets={handleUploadSuccess} />
    </>
  );
}


AttachmentList.propTypes = {
  artefactDetails: PropTypes.any,
  intr_id: PropTypes.any,
  tickets_type: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fetchTickets: PropTypes.func
};

export default AttachmentList;
